import React, { useCallback, useState } from "react";
import * as Mui from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { formResponsesPathEntry, useYDoc } from "@/hooks/useY";
import { FormResponse } from "@/types/form";
import {
  TextField,
  TextArea,
  Radio,
  Checkbox,
  DatePicker,
  Upload,
} from "@/components/filling";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { useQuery } from "@/hooks/useQuery";
import { useNavigate } from "react-router-dom";

const FormFillingPage = () => {
  const { formResponseId, startingSection } = useQuery();
  const navigate = useNavigate();
  const [responses, setResponses] = useYDoc<FormResponse[]>(
    [formResponsesPathEntry],
    []
  );

  const formResponse = responses.find((formResponse) => formResponse.id === formResponseId)!;

  const importFormResponse = useCallback(() => {
    // upload form response and set it to formResponse
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      const json = await file.text();
      const formResponse: FormResponse = JSON.parse(json);
      input.remove();

      // Check Structure
      if (
        !formResponse.id ||
        !formResponse.formDefinition ||
        !formResponse.formDefinition.sections ||
        !formResponse.formDefinition.sections.every((section) => section.id && section.title) ||
        !formResponse.responses ||
        !formResponse.responses.every((response) => response.id)
      ) {
        alert("Invalid form response file");
        return;
      }

      if (responses.some((r) => r.id === formResponse.id)) {
        alert("Form response already exists");
        return;
      }

      setResponses((responses) => [...responses, formResponse]);
      navigate(`/form-filling?formResponseId=${formResponse.id}`);
    };

    input.click();

  }, [navigate, responses, setResponses]);

  const exportFormResponse = useCallback(() => {
    // Create a json file with the content of form response
    const json = JSON.stringify(formResponse, null, 2);
    const titleFieldId = formResponse.formDefinition.titleFieldId;
    const title = formResponse.responses.find((answer) => answer.id === titleFieldId)?.value;

    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `form-response-${title}.json`;
    a.click();
    a.remove();
  }, [formResponse]);

  if (!formResponse) {
    throw new Error(`Process with id ${formResponseId} not found`);
  }

  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(
    startingSection === undefined
      ? 0
      : formResponse.formDefinition.sections.findIndex(
        (section) => section.id === startingSection
      )
  );
  const [sectionIdStack, setSectionIdStack] = useState<string[]>([]);

  const currentSection = formResponse.formDefinition.sections[currentSectionIndex];
  const branching = formResponse?.formDefinition.sections[currentSectionIndex]?.branchingRadio;
  const branchingAnswer = branching
    ? formResponse.responses.find((answer) => answer.id === branching.fieldId)?.value
    : undefined;
  const branchingOptionIndex = branchingAnswer
    ? formResponse?.formDefinition.sections[currentSectionIndex]?.fields
      ?.find((field) => field.id === branching!.fieldId)
      ?.options?.findIndex((option) => option === branchingAnswer)
    : undefined;
  const branchingOption =
    branchingOptionIndex !== undefined
      ? branching?.branchingOptions[branchingOptionIndex]
      : currentSection.nextSectionId;

  const handleNextSection = useCallback(() => {
    const nextSectionIndex =
      branchingOption !== undefined
        ? formResponse?.formDefinition.sections.findIndex(
          (section) => section.id === branchingOption
        )
        : currentSectionIndex + 1;
    setSectionIdStack([
      ...sectionIdStack,
      formResponse?.formDefinition.sections[currentSectionIndex].id,
    ]);
    setCurrentSectionIndex(nextSectionIndex);
  }, [branchingOption, currentSectionIndex, formResponse?.formDefinition, sectionIdStack]);

  const handlePreviousSection = useCallback(() => {
    const previousSectionIndex = formResponse?.formDefinition.sections.findIndex(
      (question) => question.id === sectionIdStack[sectionIdStack.length - 1]
    );
    const updatedStack = sectionIdStack.slice(0, -1);

    setSectionIdStack(updatedStack);
    setCurrentSectionIndex(previousSectionIndex);
  }, [formResponse?.formDefinition, sectionIdStack]);

  const handleChange = useCallback(
    (id: string, value: string) => {
      const newProcesses = responses.map((formResponse) => {
        if (formResponse.id === formResponseId) {
          const existingAnswer = formResponse.responses.find((a) => a.id === id);
          if (!existingAnswer) {
            return { ...formResponse, responses: [...formResponse.responses, { id, value }] };
          }
          return {
            ...formResponse,
            responses: formResponse.responses.map((answer) =>
              answer.id === id ? { ...answer, value } : answer
            ),
          };
        }
        return formResponse;
      });

      setResponses(newProcesses);
    },
    [formResponseId, responses, setResponses]
  );

  const handleSubmit = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const isFirstSection = sectionIdStack.length === 0;
  const isLastSection =
    branchingOption === undefined
      ? currentSectionIndex === formResponse.formDefinition.sections.length - 1
      : branchingOption === "end";
  const nonFilledRequiredFields = currentSection?.fields?.filter((field) => {
    const answer = formResponse.responses.find((answer) => answer.id === field.id);
    return field.required && answer?.value == undefined;
  });

  dayjs.locale(navigator.language);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={navigator.language}>
      <Mui.Container className="mt-4 mb-8">
        {/* Import/Export buttons on the right */}
        <Mui.Box display="flex" justifyContent="space-between" alignItems="center">
          <Mui.Typography variant="h4" component="h1" gutterBottom>
            Fill Form
          </Mui.Typography>
          <Mui.Box display="flex">
            <Mui.Tooltip title="Export">
              <Mui.IconButton color="primary" onClick={exportFormResponse}>
                <UploadIcon />
              </Mui.IconButton>
            </Mui.Tooltip>
            <Mui.Tooltip title="Import">
              <Mui.IconButton color="primary" onClick={importFormResponse}>
                <DownloadIcon />
              </Mui.IconButton>
            </Mui.Tooltip>
          </Mui.Box>
        </Mui.Box>
        <Mui.Box component="form">
          <Mui.Typography variant="h5" gutterBottom>
            {currentSection.title}
          </Mui.Typography>
          {currentSection.fields.map((field) => {
            const value = formResponse.responses.find(
              (answer) => answer.id === field.id
            )?.value;
            switch (field.type) {
              case "text":
                return (
                  <TextField
                    field={field}
                    value={value}
                    id={field.id}
                    onChange={handleChange}
                    key={field.id}
                  />
                );
              case "textarea":
                return (
                  <TextArea
                    field={field}
                    value={value}
                    id={field.id}
                    onChange={handleChange}
                    key={field.id}
                  />
                );
              case "radio":
                return (
                  <Radio
                    field={field}
                    value={value}
                    id={field.id}
                    onChange={handleChange}
                    key={field.id}
                  />
                );
              case "checkbox":
                return (
                  <Checkbox
                    field={field}
                    value={value}
                    id={field.id}
                    onChange={handleChange}
                    key={field.id}
                  />
                );
              case "date":
                return (
                  <DatePicker
                    field={field}
                    value={value}
                    id={field.id}
                    onChange={handleChange}
                    key={field.id}
                  />
                );
              case "upload":
                return (
                  <Upload
                    field={field}
                    value={value}
                    id={field.id}
                    onChange={handleChange}
                    key={field.id}
                  />
                );
              default:
                console.error(`Unknown field type: ${field}`);
                throw new Error("Unknown field type");
            }
          })}
          <Mui.Box mt={2} display="flex" justifyContent="space-between">
            <Mui.Button
              variant="contained"
              color="primary"
              onClick={handlePreviousSection}
              disabled={isFirstSection}
            >
              Previous
            </Mui.Button>
            <Mui.Tooltip
              title={`Please fill the following required fields: ${nonFilledRequiredFields.map((field) => field.label).join(", ")}`}
              disableHoverListener={nonFilledRequiredFields.length === 0}
            >
              <span> {/* Wrapper needed because disabled buttons don't trigger hover events */}
                {isLastSection ? (
                  <Mui.Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={nonFilledRequiredFields.length > 0}
                  >
                    Finish
                  </Mui.Button>
                ) : (
                  <Mui.Button
                    variant="contained"
                    color="primary"
                    onClick={handleNextSection}
                    disabled={nonFilledRequiredFields.length > 0}
                  >
                    Next
                  </Mui.Button>
                )}
              </span>
            </Mui.Tooltip>
          </Mui.Box>
        </Mui.Box>
      </Mui.Container>
    </LocalizationProvider>
  );
};

export default FormFillingPage;
